import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { DateRange } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import DomainSelect from './DomainSelect';
import LandingSelect from './LandingSelect';
import CountrySelect from './CountrySelect';
import TrafficSourceSelect from './TrafficSourceSelect';

// Локализация для календаря DateRange
const customLocale = {
  ...enUS,
  options: {
    ...enUS.options,
    weekStartsOn: 1
  }
};

const ErrorsFilters = ({
  domains,
  landings,
  selectedDomain,
  selectedLandings,
  selectedCountry,
  selectedDateRange,
  selectedTrafficSource,
  setSelectedDomain,
  setSelectedLandings,
  setSelectedCountry,
  setSelectedDateRange,
  setSelectedTrafficSource,
  landingOptions,
  handleFormSubmit,
  isSubmitting,
  setDomains,
  setLandings
}) => {
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const dateRangePickerRef = useRef(null);
  
  // Обработчик клика вне DateRangePicker для закрытия
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dateRangePickerRef.current && !dateRangePickerRef.current.contains(event.target)) {
        setIsDateRangePickerOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);
  
  // Обработчик изменения домена
  const handleDomainChange = (event) => {
    const selectedDomainId = event.target.value;
    setSelectedDomain(selectedDomainId);
    setSelectedLandings(['all']);
  };

  // Обработчик изменения диапазона дат
  const handleDateRangeChange = (ranges) => {
    setSelectedDateRange([ranges.selection]);
  };

  // Обработчик клика по полю диапазона дат
  const handleDateRangeFieldClick = () => {
    setIsDateRangePickerOpen(!isDateRangePickerOpen);
  };

  // Обработчик изменения источника трафика
  const handleTrafficSourceChange = (newTrafficSource) => {
    setSelectedTrafficSource(newTrafficSource);
  };

  return (
    <div className="block sticky-top bg-white shadow-sm p-4 mb-4 rounded-lg">
      <div className="grid grid-cols-4 gap-4">
        {/* Фильтры */}
        <div className="col-span-4 flex flex-wrap gap-4 items-center">
          {/* Выбор домена */}
          <DomainSelect
            domains={domains}
            selectedDomain={selectedDomain}
            onChange={handleDomainChange}
            setDomains={setDomains}
          />

          {/* Выбор лендинга */}
          <LandingSelect
            landings={landingOptions}
            selectedLandings={selectedLandings}
            onChange={setSelectedLandings}
            setLandings={setLandings}
          />

          {/* Выбор страны */}
          <CountrySelect
            selectedCountry={selectedCountry}
            onChange={setSelectedCountry}
          />

          {/* Компонент выбора источника трафика */}
          <TrafficSourceSelect
            selectedTrafficSource={selectedTrafficSource}
            onChange={handleTrafficSourceChange}
          />

          {/* Календарь выбора дат */}
          <div className="relative" ref={dateRangePickerRef}>
            <input
              type="text"
              className="px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={`${format(selectedDateRange[0].startDate, 'dd/MM/yyyy')} - ${format(
                selectedDateRange[0].endDate,
                'dd/MM/yyyy'
              )}`}
              readOnly
              onClick={handleDateRangeFieldClick}
            />
            {isDateRangePickerOpen && (
              <div className="absolute top-full left-0 z-50 mt-1 bg-white shadow-lg rounded-lg">
                <DateRange
                  ranges={selectedDateRange}
                  onChange={handleDateRangeChange}
                  locale={customLocale}
                  months={1}
                  direction="horizontal"
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                />
              </div>
            )}
          </div>
          
          {/* Кнопка поиска */}
          <button
            onClick={handleFormSubmit}
            disabled={isSubmitting}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              isSubmitting 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            }`}
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
            ) : (
              'Search'
            )}
          </button>
          
          {/* Кнопка экспорта CSV */}
          <button
            onClick={() => {
              // Вызываем функцию экспорта, определенную в родительском компоненте
              if (typeof window.exportErrorsToCSV === 'function') {
                window.exportErrorsToCSV();
              }
            }}
            className="px-4 py-2 flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <span>Export CSV</span>
          </button>

          {/* Ссылка на управление категориями ошибок */}
          <a 
            href="/errors/categories"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors ml-auto"
            title="Manage error categories"
          >
            Manage Categories
          </a>
        </div>
      </div>
    </div>
  );
};

export default ErrorsFilters;